import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Plus,
  Package,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  CalendarDays,
  Archive,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { NewOrderDialog } from "../components/NewOrderDialog";
import { OrderCard } from "../components/OrderCard";
import { motion, AnimatePresence } from "framer-motion";

const COMPLETED_STATUSES = ["Complete", "Shipped"];

// Win98: status renders as a plain inset .win-well chip (matches Quotes/Invoices);
// the status name text carries the meaning, so no colored pill is needed.
const getStatusBadgeColor = () => "";

export function OrdersPage() {
  const { user } = useAuth();
  const canEdit = user && (user.role === "admin" || user.role === "accountant");
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    overdue: 0,
    by_status: {},
    printhq: { enabled: false, reachable: false, count: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [ordersData, statusesData, sourcesData, statsData] = await Promise.all([
        api.get("/orders"),
        api.get("/config/order-statuses"),
        api.get("/config/order-sources"),
        api.get("/orders/stats"),
      ]);
      setOrders(ordersData);
      setStatuses(statusesData);
      setSources(sourcesData);
      setStats(statsData);
    } catch (error) {
      toast.error("Failed to load order data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOrderCreated = () => {
    fetchData();
    setDialogOpen(false);
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}`, { status: newStatus });
      toast.success(`Order moved to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to update order");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      await api.delete(`/orders/${orderId}`);
      toast.success("Order deleted");
      fetchData();
    } catch (error) {
      toast.error(error.message || "Failed to delete order");
    }
  };

  // Filter active orders
  const activeOrders = orders.filter((o) => !COMPLETED_STATUSES.includes(o.status));

  // A PrintHQ-owned order can carry a status this org has not configured in Settings. Give any
  // such status its own column instead of letting the order vanish from the board entirely.
  const configuredActive = statuses.filter((s) => !COMPLETED_STATUSES.includes(s.name));
  const unconfiguredStatuses = [...new Set(activeOrders.map((o) => o.status))].filter(
    (name) => name && !configuredActive.some((s) => s.name === name)
  );
  const activeStatuses = [
    ...configuredActive,
    ...unconfiguredStatuses.map((name) => ({ id: `unconfigured:${name}`, name })),
  ];

  // Group orders by status
  const ordersByStatus = activeStatuses.reduce((acc, status) => {
    acc[status.name] = activeOrders.filter((o) => o.status === status.name);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse space-y-4 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/20 mx-auto" />
          <p className="text-muted-foreground">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">Orders</h1>
          <p className="text-muted-foreground mt-1">Track your commission orders</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/orders/completed")} className="flex items-center gap-2">
            <Archive className="w-4 h-4" />
            Completed
          </Button>
          {canEdit && (
            <Button onClick={() => setDialogOpen(true)} className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              New Order
            </Button>
          )}
        </div>
      </div>

      {/* Most orders on this board are owned by PrintHQ and read through live, so say so when
          that read failed and the list is a cached snapshot rather than current. */}
      {stats.printhq?.enabled && !stats.printhq?.reachable && (
        <div className="win-well p-3 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="text-sm font-medium text-foreground">PrintHQ is unreachable</p>
            <p className="text-sm text-muted-foreground">
              Orders owned by PrintHQ may be out of date, and status changes to them will fail
              until it is back.
            </p>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 win-well flex items-center justify-center">
                <Package className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Orders</p>
                <p className="text-2xl font-bold font-mono">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 win-well flex items-center justify-center">
                <Clock className="w-6 h-6 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold font-mono">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 win-well flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-500" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold font-mono">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 win-well flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-500" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Overdue</p>
                <p className="text-2xl font-bold font-mono">{stats.overdue}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Orders - Kanban */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary" strokeWidth={1.5} />
            Active Orders
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {activeOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="w-16 h-16 text-muted-foreground/50 mb-4" strokeWidth={1} />
              <p className="text-muted-foreground text-lg">No active orders</p>
              <p className="text-muted-foreground/70 text-sm mt-1">Create your first order to get started</p>
              {canEdit && (
                <Button onClick={() => setDialogOpen(true)} className="mt-6">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Order
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {activeStatuses.map((status) => (
                <div key={status.id}>
                  <div className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center gap-2">
                      <Badge className={`${getStatusBadgeColor(status.name)} rounded-md px-2 py-1`}>
                        {status.name}
                      </Badge>
                      <span className="text-muted-foreground text-sm font-mono">
                        {ordersByStatus[status.name]?.length || 0}
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground/50" />
                  </div>
                  <div className="space-y-3 min-h-[200px] p-2 rounded-2xl bg-muted/30 border border-border/40">
                    <AnimatePresence mode="popLayout">
                      {ordersByStatus[status.name]?.map((order) => (
                        <motion.div
                          key={order.id}
                          layout
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ duration: 0.2 }}
                        >
                          <OrderCard
                            order={order}
                            statuses={statuses}
                            sources={sources}
                            onStatusChange={handleStatusChange}
                            onDelete={handleDeleteOrder}
                            onUpdate={fetchData}
                            canEdit={canEdit}
                          />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                    {(!ordersByStatus[status.name] || ordersByStatus[status.name].length === 0) && (
                      <div className="flex items-center justify-center h-24 text-muted-foreground/50 text-sm">
                        No orders
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Order Dialog */}
      {canEdit && (
        <NewOrderDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          statuses={statuses}
          sources={sources}
          onSuccess={handleOrderCreated}
        />
      )}
    </div>
  );
}
